import { privateAttributes } from "@/cart/cartAttributes";
import type {
  CartAndLineAttributeMap,
  CartAndLineAttributesInput,
  CartStateGetSet,
} from "@/cart/types";
import GRAPHQL_CLIENT from "@/graphql/client";
import type { Cart } from "@/graphql/storefront/generated";
import posthog from "posthog-js";

export const setCartAndLineAttributes = ({ get, set }: CartStateGetSet) => {
  let updateQueue: Promise<void> = Promise.resolve();

  const toAttributeArray = (attributes: CartAndLineAttributeMap) => {
    /* The map can hold null/undefined mid-form and always has: the wire shape
       is unchanged, the schema's required String is asserted as before. */
    return Object.entries(attributes).map(([key, value]) => ({
      key,
      value: value as string,
    }));
  };

  const updateAttributes = async (
    lineId: string | undefined,
    attributes: CartAndLineAttributesInput,
  ) => {
    const { cartId, cart } = get();
    if (!cartId) {
      console.error("setCartAndLineAttributes: no cart ID");
      return;
    }

    const { cartAttributes, lineAttributes } = attributes;
    const line = cart?.lines.edges.find(({ node }) => node.id === lineId)?.node;
    // The mutation replaces each set outright; the callers (the gift form,
    // the referral marker) only ever mean to write their own keys.
    const cartAttributesArray = [
      ...privateAttributes(cart?.attributes).filter(
        ({ key }) => !(key in cartAttributes),
      ),
      ...toAttributeArray(cartAttributes),
    ];
    const lineAttributesArray = [
      ...privateAttributes(line?.attributes).filter(
        ({ key }) => !(key in lineAttributes),
      ),
      ...toAttributeArray(lineAttributes),
    ];

    // Keys only, never values: the gift form puts the recipient's name and
    // email address in these attributes, and analytics is not a place for it.
    posthog.capture("update_cart_and_line_attributes", {
      lineId,
      cartAttributeKeys: cartAttributesArray.map(({ key }) => key),
      lineAttributeKeys: lineAttributesArray.map(({ key }) => key),
      cartId,
    });

    // Single atomic mutation that updates both cart and line item attributes
    const result = await GRAPHQL_CLIENT.UpdateCartAndLineAttributes({
      cartId,
      cartAttributes: cartAttributesArray,
      lines: lineId
        ? [
            {
              id: lineId,
              attributes: lineAttributesArray,
            },
          ]
        : [],
    });

    // Check for errors in either mutation
    const cartErrors = result.updateCartAttributes?.userErrors || [];
    const lineErrors = result.updateLineAttributes?.userErrors || [];
    const allErrors = [...cartErrors, ...lineErrors];

    if (allErrors.length > 0) {
      console.error("setCartAndLineAttributes errors:", allErrors);
      throw new Error(
        `Failed to update attributes: ${allErrors.map((e) => e.message).join(", ")}`,
      );
    }

    // Use the cart from the line attributes update as it's the final state
    const updatedCart =
      result.updateLineAttributes?.cart || result.updateCartAttributes?.cart;

    if (!updatedCart) {
      throw new Error(
        "setCartAndLineAttributes: no cart returned from mutation",
      );
    }

    set({ cartId: updatedCart.id, cart: updatedCart as Cart });
  };

  return (
    lineId: string | undefined,
    attributes: CartAndLineAttributesInput,
  ) => {
    updateQueue = updateQueue
      .catch(() => undefined)
      .then(() => updateAttributes(lineId, attributes));
    return updateQueue;
  };
};
